import React, {useState, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {getExtension} from "../../../../helpers/helper";
import SettingsService from "../../../../services/SettingsService/SettingsService";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import {useSetRecoilState} from 'recoil';
import {alertState} from '../../../../recoil-store/recoil-state';
import "./Port.css";

/**
 * @Description This function lets you return Logging.
 * @Returns     Component
 * @Author      ibrahim AKIN
 */
function Port(props) {
    const {t} = useTranslation();
    const label = "CONTENT.SETTINGS.PORT.";
    const [file, setFile] = useState();
    const [password, setPassword] = useState();
    const [updateFields, setUpdateFields] = useState({});
    const [formFields, setFormFields] = useState({
        httpPort: "",
        serviceHttpPort: "",
        isHttps: false,
        validate: false,
        httpsPort: "",
        serviceHttpsPort: "",
        mateAgentPort: "",
        sslCertPassword: "",
        sslCertName: ""
    });
    const [refresh, setRefresh] = useState(false);
    const setAlert = useSetRecoilState(alertState);

    async function fetchData() {
        const response = await SettingsService.getSettings();

        if (response) {
            const fields = {
                httpPort: response?.data.http_port,
                httpsPort: response?.data.https_port,
                serviceHttpPort: response?.data.service_http_port,
                serviceHttpsPort: response?.data.service_https_port,
                mateAgentPort: response?.data.ma_port,
                sslCertName: response?.data.ssl_cert_name ? response?.data.ssl_cert_name : "",
                isHttps: response?.data.ssl_enabled ? true : formFields.isHttps,
                validate: response?.data.ssl_enabled ? true : formFields.validate
            }

            if (response?.data.ssl_cert_name) {
                const fileName = {name: response?.data.ssl_cert_name}
                setFile({...file, ...fileName})
            }

            setPassword(response?.data.ssl_cert_password);
            setFormFields(formFields => ({...formFields, ...fields}));
            setRefresh(true);
        }
    }

    useEffect(() => {
        fetchData();
    }, [refresh]);

    const handleFileChange = (e) => {
        if (e.target.files[0]?.name) {
            let extension = getExtension(e.target.files[0]?.name);
            if (extension === "pfx") {
                setFile(e.target.files[0]);
            }
        }
    };

    const fieldMatcher = (param) => {
        const fields = {
            "httpPort": "http_port",
            "httpsPort": "https_port",
            "serviceHttpPort": "service_http_port",
            "serviceHttpsPort": "service_https_port",
            "maPort": "ma_port",
            "isHttps": "ssl_enabled"
        };

        return fields[param];
    }

    const handleUpdateFields = (value, param) => {
        const paramName = fieldMatcher(param);
        if (formFields[param] !== value) {
            updateFields[paramName] = value;
            setUpdateFields(updateFields)
        } else {
            delete updateFields[paramName];
            setUpdateFields(updateFields);
        }
    }

    const setStateField = (value, param) => {
        const field = {[param]: value}
        if (param === "sslCertPassword")
            field.validate = false;

        setFormFields({...formFields, ...field});
        handleUpdateFields(value, param);
    }

    const handleChange = (e) => {
        setStateField(e.target.checked, "isHttps");
        if (e.target.checked) {
            handleUpdateFields(formFields.httpsPort, "httpsPort");
            handleUpdateFields(formFields.serviceHttpsPort, "serviceHttpsPort");
        } else {
            handleUpdateFields(formFields.httpPort, "httpPort");
            handleUpdateFields(formFields.serviceHttpPort, "serviceHttpPort");
        }
    };

    const handleClick = (e) => {
        setFile("");
    };

    const validateForm = () => {
        if (formFields.isHttps) {
            if (!formFields.httpsPort) return false;
        }

        return !(!formFields.httpPort || !formFields.serviceHttpPort || !formFields.mateAgentPort);
    };

    async function handleSave() {
        // save data
        const isValid = validateForm();

        if (isValid) {
            if (formFields.isHttps) {
                delete updateFields["http_port"];
                delete updateFields["service_http_port"];
            } else {
                delete updateFields["https_port"];
                delete updateFields["service_https_port"];
            }

            if (file && formFields.sslCertPassword !== password && !formFields.validate) {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t('MESSAGES.SETTINGS.PLEASE-VALIDATE-CERTIFICATE'),
                    severity: 'error'
                }));
                return false;
            }

            const response = await SettingsService.updateSettings(updateFields);

            if (response) {
                if (response?.status) {
                    //success
                    setRefresh(true);
                    setAlert(alert => ({
                        ...alert,
                        show: true,
                        message: t('MESSAGES.SETTINGS.' + response.message),
                        severity: 'success'
                    }));
                }
            }
        } else {
            // Fields Empty message!
            setAlert(alert => ({...alert, show: true, message: t("GENERAL.NOT-NULL"), severity: 'error'}));
        }
    }

    async function handleValidateSSL() {
        if (file?.name?.length) {
            const data = {
                file,
                password: formFields.sslCertPassword,
            };
            const response = await SettingsService.validateSslSettings(data);
            if (response) {
                if (response?.status) {
                    setAlert(alert => ({
                        ...alert,
                        show: true,
                        message: t('MESSAGES.SETTINGS.' + response.message),
                        severity: 'success'
                    }));
                    setStateField(response.status, "validate");
                } else {
                    setAlert(alert => ({
                        ...alert,
                        show: true,
                        message: t('MESSAGES.SETTINGS.' + response.message),
                        severity: 'error'
                    }));
                    setStateField(response.status, "validate");
                }
            }
        } else {
            // Fields Empty message!
            setAlert(alert => ({...alert, show: true, message: t("GENERAL.NOT-NULL"), severity: 'error'}));

        }
    }

    const restoreDefault = () => {
        // TODO restore default
    }
    return (
        <div className="sgeneral">
            <div>{t(label + "PORT")}</div>
            <div className="port-grid">
                <div>
                    <div className="port-label">
                        {t(label + "SSL-ENABLED")}
                    </div>
                    <div className="sgeneral-divider"/>
                    <label className="switch small">
                        <input type="checkbox" onChange={handleChange} checked={formFields.isHttps}/>
                        <span className="slider round red"/>
                    </label>
                </div>
                <div>
                    <div className="port-label">{t(label + "MATE")} *</div>
                    <div className="sgeneral-divider"/>
                    <input
                        className="port-input"
                        type="number"
                        onChange={(e) => setStateField(e.target.value, "mateAgentPort")}
                        value={formFields.mateAgentPort}
                        min="0"
                    />
                </div>
                <div>
                    <div className="port-label">{t(label + "WEB")} *</div>
                    <div className="sgeneral-divider"/>
                    <input
                        className="port-input"
                        type="number"
                        onChange={(e) => setStateField(e.target.value, formFields.isHttps ? "httpsPort" : "httpPort")}
                        value={formFields.isHttps ? formFields.httpsPort : formFields.httpPort}
                        min="0"
                    />
                </div>
                <div>
                    <div className="port-label">
                        {t(label + "SERVICE")}&nbsp;&nbsp;&nbsp;
                    </div>
                    <div className="sgeneral-divider"/>
                    <input
                        className="port-input"
                        type="number"
                        min="0"
                        onChange={(e) => setStateField(e.target.value, formFields.isHttps ? "serviceHttpsPort" : "serviceHttpPort")}
                        value={formFields.isHttps ? formFields.serviceHttpsPort : formFields.serviceHttpPort}
                    />
                </div>
            </div>
            <div>
                <div className="port-label">{t(label + "SSL")} *</div>
                <div className="sgeneral-divider ssl"/>
                <div className="port-ssl">
                    <div style={{height: 28}}>
                        <div className="custom-file">
                            <input
                                type="file"
                                accept=".pfx"
                                className={!formFields.isHttps ? "custom-file-input disabled-input" : "custom-file-input"}
                                id="sgeneral-customFile"
                                onChange={handleFileChange}
                                disabled={!formFields.isHttps}
                            />
                            <label
                                className={!formFields.isHttps ? "custom-file-label disabled-input" : "custom-file-label"}
                                htmlFor="sgeneral-customFile"
                            >
                                {file?.name ? file?.name : t(label + "CHOOSE")}
                            </label>
                            <button disabled={!formFields.isHttps}
                                    className={!formFields.isHttps ? "disabled-input" : ""}
                                    onClick={handleClick}>&#x02A2F;</button>
                        </div>
                        <div
                            style={{display: "block", marginRight: 10, fontStyle: "italic"}}
                        >
                            <small style={{fontSize: 10}}>
                                {t(label + "FILE-CONTROL")}
                            </small>
                        </div>
                    </div>
                    <input
                        className={formFields.isHttps ? "port-input" : "port-input disabled-input"}
                        type="password"
                        onChange={(e) => setStateField(e.target.value, "sslCertPassword")}
                        placeholder="password"
                        disabled={!formFields.isHttps}
                    />
                    <button
                        onClick={handleValidateSSL}
                        className={formFields.isHttps ? "sgeneral-save" : "sgeneral-save disabled-input"}
                        style={{marginRight: 5}}
                        disabled={!formFields.isHttps}
                    >
                        {t(label + "VALIDATE")}
                    </button>
                    {formFields.validate === false ? (
                        <ErrorOutlineIcon style={{width: 16, height: 16, color: "red"}}/>
                    ) : (
                        <CheckCircleOutlineIcon
                            style={{width: 16, height: 16, color: "green"}}
                        />
                    )}
                </div>
            </div>
            <div className="sgeneral-divider"/>
            <div className="bottom-row">
                <div>
                    <button className="sgeneral-save" onClick={handleSave}>
                        {t(label + "SAVE")}
                    </button>
                    <button className="cancel-button">{t(label + "CANCEL")}</button>
                </div>
                <button className="button-restore" onClick={restoreDefault}>
                    {t(label + 'RESTORE')}
                </button>
            </div>
        </div>
    );
}

export default Port;
